import type { ErrorEvent, EventHint } from '@sentry/react'

const TRANSLATED_ROOT_CLASSES = ['translated-ltr', 'translated-rtl']
const TRANSLATED_TEXT_SELECTOR =
  'font[style*="vertical-align: inherit"] > font[style*="vertical-align: inherit"]'
const ADMIN_REGIONS = ['aside', 'header', 'main']

export function isBrowserTranslationActive(
  documentObject: Document | undefined = globalThis.document
): boolean {
  if (!documentObject?.documentElement) {
    return false
  }

  if (
    TRANSLATED_ROOT_CLASSES.some((className) =>
      documentObject.documentElement.classList.contains(className)
    )
  ) {
    return true
  }

  const translatedRegionCount = ADMIN_REGIONS.filter((region) =>
    documentObject.querySelector(`${region} ${TRANSLATED_TEXT_SELECTOR}`)
  ).length

  return translatedRegionCount >= 2
}

type NavigableRouter<TOptions> = {
  navigate: (options: TOptions) => Promise<void>
}

export function enableBrowserTranslationSafeNavigation<
  TOptions extends { reloadDocument?: boolean },
>(router: NavigableRouter<TOptions>, documentObject: Document | undefined = globalThis.document) {
  const navigate = router.navigate.bind(router)

  router.navigate = (options) => {
    if (!isBrowserTranslationActive(documentObject)) {
      return navigate(options)
    }

    return navigate({
      ...options,
      reloadDocument: true,
    })
  }
}

function originalErrorName(hint: EventHint | undefined): string | undefined {
  const error = hint?.originalException

  return typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string'
    ? error.name
    : undefined
}

function originalErrorMessage(hint: EventHint | undefined): string | undefined {
  const error = hint?.originalException

  return typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
    ? error.message
    : undefined
}

function isTranslatedDomMutationError(event: ErrorEvent, hint: EventHint | undefined): boolean {
  const exception = event.exception?.values?.at(-1)
  const type = originalErrorName(hint) ?? exception?.type
  const message = originalErrorMessage(hint) ?? exception?.value ?? event.message ?? ''

  return (
    (type === 'NotFoundError' || type === 'HierarchyRequestError') &&
    /(?:removeChild|insertBefore).*(?:not a child|child of this node|node before which)/i.test(
      message
    )
  )
}

export function annotateBrowserTranslationError(
  event: ErrorEvent,
  hint: EventHint | undefined,
  documentObject: Document | undefined = globalThis.document
): ErrorEvent {
  if (!isBrowserTranslationActive(documentObject) || !isTranslatedDomMutationError(event, hint)) {
    return event
  }

  return {
    ...event,
    tags: {
      ...event.tags,
      browser_translation: 'true',
    },
    fingerprint: ['admin-browser-translation-dom-mutation'],
  }
}
